/**
  createUploadSessionSlice 实现：实现所有会话管理方法，包括：
  createUploadSession - 创建新会话
  getOrCreateUploadSession - 获取或创建会话
  addUploadTasks - 添加上传任务（关键：追加而非清空）
  updateUploadTask - 更新任务状态
  removeUploadTask - 删除任务
  clearUploadSession - 清空会话
  getUploadSessionTasks - 获取会话任务
  getAllUploadTasks - 获取所有任务（用于全局进度面板）
 */

import { StateSlice } from "@/stores/appStore";
import { UploadItem, UploadSession } from "@/components/paper-upload/core/types";
import { uuid } from "@/lib/uuid";

export type UploadSessionSlice = {
  uploadSessions: Record<string, UploadSession>;
  activeUploadSessionId: string | null;
  createUploadSession: (route?: string, subjectId?: string) => string;
  getOrCreateUploadSession: (route?: string, subjectId?: string) => string;
  setActiveUploadSession: (sessionId: string | null) => void;
  addUploadTasks: (sessionId: string, tasks: UploadItem[]) => void;
  updateUploadTask: (sessionId: string, taskId: string, updates: Partial<UploadItem>) => void;
  removeUploadTask: (sessionId: string, taskId: string) => void;
  clearUploadSession: (sessionId: string) => void;
  clearAllUploadSessions: () => void;
  getUploadSessionTasks: (sessionId: string) => UploadItem[];
  getAllUploadTasks: () => UploadItem[];
};

export const createUploadSessionSlice: StateSlice<UploadSessionSlice> = (set, get) => ({
  uploadSessions: {},
  activeUploadSessionId: null,

  createUploadSession: (route = "", subjectId) => {
    const sessionId = uuid();
    set((state) => {
      state.uploadSessions[sessionId] = {
        id: sessionId,
        tasks: [],
        subjectId,
        route,
        createdAt: Date.now(),
      };
      state.activeUploadSessionId = sessionId;
    });
    return sessionId;
  },

  getOrCreateUploadSession: (route = "", subjectId) => {
    if (route) {
      const existing = Object.values(get().uploadSessions).find((session) => session.route === route);
      if (existing) {
        return existing.id;
      }
    }
    return get().createUploadSession(route, subjectId);
  },

  setActiveUploadSession: (sessionId) => {
    set((state) => {
      state.activeUploadSessionId = sessionId;
    });
  },

  addUploadTasks: (sessionId, tasks) => {
    if (!tasks.length) {
      return;
    }
    if (!sessionId) {
      console.warn("addUploadTasks: sessionId is empty, tasks will not be added");
      return;
    }
    set((state) => {
      const session = state.uploadSessions[sessionId];
      if (!session) {
        console.warn(`addUploadTasks: session ${sessionId} not found, tasks will not be added`);
        return;
      }
      session.tasks.push(...tasks);
    });
  },

  updateUploadTask: (sessionId, taskId, updates) => {
    set((state) => {
      const session = state.uploadSessions[sessionId];
      if (!session) {
        return;
      }
      const target = session.tasks.find((task) => task.id === taskId);
      if (!target) {
        return;
      }
      Object.assign(target, updates);
    });
  },

  removeUploadTask: (sessionId, taskId) => {
    set((state) => {
      const session = state.uploadSessions[sessionId];
      if (!session) {
        return;
      }
      session.tasks = session.tasks.filter((task) => task.id !== taskId);
    });
  },

  clearUploadSession: (sessionId) => {
    set((state) => {
      delete state.uploadSessions[sessionId];
      if (state.activeUploadSessionId === sessionId) {
        state.activeUploadSessionId = null;
      }
    });
  },

  clearAllUploadSessions: () => {
    set((state) => {
      state.uploadSessions = {};
      state.activeUploadSessionId = null;
    });
  },

  getUploadSessionTasks: (sessionId) => {
    return get().uploadSessions[sessionId]?.tasks ?? [];
  },

  getAllUploadTasks: () => {
    return Object.values(get().uploadSessions).flatMap((session) => session.tasks);
  },
});

